import { isSuccess } from '@devexperts/remote-data-ts'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import { SummaryCost } from '@/components/Reservation/Costs'

import { SummaryReservation } from '@/components/Reservation/List/index.tsx'
import { SpotType } from '@/models/cart.ts'
import { LicenseBookingMode } from '@/models/license.ts'
import { QuoteReservation } from '@/models/quote.ts'
import { isKioskSelector } from '@/store/appSlice.ts'
import { BookingAvailability } from '@/store/bookingAvailabilitySlice.ts'
import { cartSlice } from '@/store/cartSlice'
import { cartDetailReservationsSelector } from '@/store/cartSlice/selectors/selectCartDetailReservations.ts'
import {
  selectTotal,
  selectTotalPrice,
  selectWeatherInsurancePrice,
} from '@/store/cartSlice/selectors/selectTotalPrice.ts'
import { licenseSlice } from '@/store/licenseSlice'
import { mapElementsSelector } from '@/store/mapSlice.ts'

import { getMapElementKey } from '@/utils/getMapElementKey.ts'
import { SummaryProductSelected } from '@/utils/recap.ts'
import {
  getGroupLabel,
  getItemGroups,
} from '@/utils/timeSchedule/groupItems.ts'
import { adjustEndTime, isFullDay } from '@/utils/timeSchedules.ts'
import {
  dayDifferenceSelector,
  insertPeriodSlice,
} from '../store/insertPeriodSlice.ts'
import { getReservationSpotId } from '../utils/getReservationSpotId.ts'
import { useAppSelector } from './store.ts'
import { useCartSelectedProducts } from './useCartSelectedProducts.ts'
import { useCartSelectedServices } from './useCartSelectedServices.ts'
import { useMapSpotName } from './useMapSpotName.ts'

export const useCartRecapData = (isUsingCartInfoData?: boolean) => {
  const { i18n, t } = useTranslation()

  const license = useAppSelector(licenseSlice.selectors.license)
  const bookingMode = license?.bookingMode
  const dayDifference = useAppSelector(dayDifferenceSelector)
  const insertPeriod = useAppSelector(insertPeriodSlice.selectors.period)
  const isKiosk = useAppSelector(isKioskSelector)
  const bookingPeriod = useAppSelector(
    insertPeriodSlice.selectors.bookingPeriod
  )
  const cartDetail = useAppSelector(cartSlice.selectors.cartDetail)
  const cartReservations = useAppSelector(cartSlice.selectors.cartReservations)
  const cartDetailPrices = useAppSelector(cartSlice.selectors.cartDetailPrices)
  const selectedBookingType = useAppSelector(
    cartSlice.selectors.selectedBookingType
  )
  const isWeatherInsuranceAddedToCart = useAppSelector(
    cartSlice.selectors.isWeatherInsuranceAddedToCart
  )
  const quote = useAppSelector(cartSlice.selectors.quote)
  const quoteWithDiscount = useAppSelector(
    cartSlice.selectors.quoteWithDiscount
  )
  const showWeatherInsurancePrice =
    isWeatherInsuranceAddedToCart ||
    (isSuccess(cartDetail) && !!cartDetail.value.cart.weatherPolicy)

  const formattedCartInfoReservations = useAppSelector(
    cartDetailReservationsSelector
  )
  const totalPrice = useAppSelector((state) =>
    selectTotalPrice(state, isUsingCartInfoData)
  )
  const total = useAppSelector((state) =>
    selectTotal(state, isUsingCartInfoData)
  )
  const weatherInsurancePrice = useAppSelector((state) =>
    selectWeatherInsurancePrice(state, isUsingCartInfoData)
  )

  const reservations = isUsingCartInfoData
    ? formattedCartInfoReservations
    : cartReservations

  const selectedProducts = useCartSelectedProducts(reservations)

  const { promoService } = useReservationServicesWithPromo(
    reservations,
    i18n.language
  )

  const today = DateTime.now().toISODate()

  const isRefundable = useAppSelector(
    licenseSlice.selectors.isBookingRefundable
  )

  const isQuoteWithDiscount = isSuccess(quoteWithDiscount)
  const paymentLinkAmount = isSuccess(cartDetail)
    ? cartDetail.value.paymentLink?.amount
    : undefined
  const hasPaymentLink = typeof paymentLinkAmount === 'number'

  const mapElements = useAppSelector(mapElementsSelector)
  const { getMapSpotName } = useMapSpotName()

  const summaryReservations = reservations.reduce<SummaryReservation[]>(
    (acc, reservation, index) => {
      const cartReservation = cartReservations.find(
        (r) =>
          r.spotType === reservation.spotType &&
          r.spotName === reservation.spotName
      )

      if (!cartReservation) {
        return acc
      }

      const spotId = getReservationSpotId(reservation)

      const mapElement = isSuccess(mapElements)
        ? mapElements.value[
            getMapElementKey(reservation.spotType, reservation.spotName)
          ]
        : null

      const spot = mapElement ?? {
        name: reservation.spotName,
        sectorId: reservation.sectorId,
        sectorName: reservation.sectorName,
        subType: reservation.spotType,
      }

      const title = getMapSpotName(spot, {
        shouldShowSpotName: isUsingCartInfoData
          ? reservation.shouldShowSpotName
          : bookingMode === LicenseBookingMode.MAP,
      })
      const services = Object.keys(cartReservation.boughtServices).reduce<
        SummaryProductSelected[]
      >((acc, key) => {
        const serviceId = Number(key)

        const boughtServices = cartReservation.boughtServices[serviceId]
        if (!boughtServices) {
          return acc
        }

        const groupItems = getItemGroups(
          cartReservations,
          { spotName: reservation.spotName, spotType: reservation.spotType },
          serviceId
        )

        const services = groupItems.reduce<SummaryProductSelected[]>(
          (acc, group) => {
            const t = boughtServices[`${group.startTime} - ${group.endTime}`]
            if (!t) {
              return acc
            }

            function getName() {
              const fullDay = isFullDay(group)

              const suffix = t.timeSchedule.name
                ? ` · ${t.timeSchedule.name}`
                : ''
              const baseName = t.serviceName + suffix

              return `${baseName}${fullDay ? '' : ` · ${group.startTime}-${adjustEndTime(group.endTime)}`}`
            }

            const subLabel = getGroupLabel({
              dayDifference,
              group,
              license,
              locale: i18n.language,
            })

            acc.push({
              id: serviceId,
              isExtra: true,
              isGift: false,
              name: getName(),
              quantity: group.quantity,
              spotId: getReservationSpotId(reservation),
              ...(subLabel !== null ? { subLabel } : {}),
            })

            return acc
          },
          []
        )

        return acc.concat(...services)
      }, [])

      acc.push({
        productsSelected: [
          ...selectedProducts
            .filter((r) => r.spotId === spotId)
            .map((product) => ({
              isExtra: false,
              isGift: false,
              name: product.name,
              quantity: product.quantity,
            })),
          ...services,
          ...(promoService && index === 0 ? [promoService] : []),
        ],
        spotType: reservation.spotType as SpotType,
        title: title,
      })

      return acc
    },
    []
  )

  const getFeesPrice = () => {
    if (isUsingCartInfoData && isSuccess(cartDetail)) {
      return cartDetail.value.cart.totalFeesValue
    }

    if (isQuoteWithDiscount) {
      return quoteWithDiscount.value.fees
    }

    if (isSuccess(quote)) {
      return quote.value.fees
    }

    return 0
  }

  const getServicesPrice = () => {
    if (isUsingCartInfoData) {
      return cartDetailPrices?.totalServicesValue || 0
    }

    return 0
  }

  function getTotalSpotPrice() {
    if (isQuoteWithDiscount) {
      return Object.values(
        quoteWithDiscount.value.reservationPrices
      ).reduce<number>((acc, value) => acc + value, 0)
    }

    if (isSuccess(quote) && quote.value.reservationPrices) {
      const total = Object.values(quote.value.reservationPrices).reduce<number>(
        (acc, value) => acc + value,
        0
      )

      if (quote.value.offer) {
        return Object.values(quote.value.offer).reduce<number>((acc, offer) => {
          if (offer.value < 0) {
            return acc - offer.value
          }
          return acc
        }, total)
      }

      return total
    }

    return totalPrice - getServicesPrice() - getFeesPrice()
  }

  function getTotalSpotPriceFromCartDetailPrices(): number {
    if (hasPaymentLink && typeof cartDetailPrices?.totalPrice === 'number') {
      return cartDetailPrices.totalPrice
    }
    return cartDetailPrices?.totalSpotsValue || 0
  }

  const totalSpotPrice = isUsingCartInfoData
    ? getTotalSpotPriceFromCartDetailPrices()
    : getTotalSpotPrice()

  const servicesPrice = getServicesPrice()

  let discountPrice

  if (isUsingCartInfoData) {
    discountPrice = cartDetailPrices?.totalDiscountValue
  } else if (isQuoteWithDiscount) {
    discountPrice = quoteWithDiscount.value.discountValue
  }

  const hasDiscountApplied = isUsingCartInfoData
    ? cartDetailPrices && cartDetailPrices.totalDiscountValue > 0
    : isQuoteWithDiscount

  const fees = getFeesPrice()

  const costs: SummaryCost[] = [
    {
      name:
        selectedBookingType === BookingAvailability.TICKETS
          ? t('common.tickets')
          : t('common.spot', {
              count: cartReservations.length,
            }),
      price: totalSpotPrice + servicesPrice,
    },
    ...(showWeatherInsurancePrice
      ? [
          {
            name: t('extra.weatherInsurance.title'),
            price: weatherInsurancePrice,
          },
        ]
      : []),
    ...(fees > 0 && !isKiosk
      ? [
          {
            hasTooltip: true,
            name: t('common.serviceCosts'),
            price: fees,
          },
        ]
      : []),
    ...(hasDiscountApplied && typeof discountPrice === 'number'
      ? [
          {
            isDiscount: true,
            name: t('insertDataForm.promo.discountCode'),
            price: discountPrice,
          },
        ]
      : []),
  ]

  const deposit =
    paymentLinkAmount !== undefined && total - paymentLinkAmount > 0
      ? paymentLinkAmount
      : 0

  return {
    beachName: license?.name || '',
    bookingPeriod,
    costs,
    deposit,
    endDate: insertPeriod?.end || today,
    isRefundable: !!isRefundable,
    location: license?.beach.city || '',
    region: license?.beach.region || '',
    reservations: summaryReservations,
    startDate: insertPeriod?.start || today,
    total: paymentLinkAmount ?? total,
  }
}

function useReservationServicesWithPromo(
  reservations: QuoteReservation[],
  language: string
) {
  const promotionalService = useAppSelector(
    cartSlice.selectors.promotionalService
  )

  const reservationServices = useCartSelectedServices(reservations, language)

  if (!promotionalService) {
    return {
      promoService: null,
      reservationServices,
    }
  }

  // Finds the first reservation that has the same service id of the promotional service
  const sameServiceIndex = reservationServices.findIndex(
    (service) => service.id === promotionalService.id
  )

  if (sameServiceIndex > 0) {
    return {
      promoService: null,
      reservationServices: reservationServices.map((service, index) => {
        if (sameServiceIndex === index) {
          return {
            ...service,
            isExtra: true,
            isGift: true,
            quantity: service.quantity + 1,
          }
        }

        return service
      }),
    }
  }

  return {
    promoService: {
      id: promotionalService.id,
      isExtra: true,
      isGift: true,
      name: promotionalService.name,
      quantity: 1,
    },
    reservationServices,
  }
}
